import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { LoaderCircle, LockKeyhole } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { z } from 'zod'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { isApiError, sanitizeNextPath } from '@/lib/api'
import { DEMO_CREDENTIALS, authKeys, currentUserQueryOptions, login } from '@/lib/auth'

const loginSearchSchema = z.object({
  /** 登录后回跳的站内路径;非法值在 sanitizeNextPath 里被降级成 /dashboard。 */
  next: z.string().optional(),
})

export const Route = createFileRoute('/login')({
  validateSearch: loginSearchSchema,
  beforeLoad: async ({ context, search }) => {
    // 已登录就别停在登录页。后端没起时这里会抛错,吞掉当未登录处理 —— 让登录页永远可达,
    // 真正的连接失败在提交时以中文错误呈现。
    let alreadySignedIn: boolean
    try {
      alreadySignedIn = (await context.queryClient.ensureQueryData(currentUserQueryOptions())) !== null
    } catch {
      alreadySignedIn = false
    }
    if (alreadySignedIn) {
      throw redirect({ href: sanitizeNextPath(search.next) })
    }
  },
  component: LoginPage,
})

function LoginPage() {
  const search = Route.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: async (user) => {
      queryClient.setQueryData(authKeys.me(), user)
      await queryClient.invalidateQueries({ queryKey: authKeys.all })
      await navigate({ href: sanitizeNextPath(search.next), replace: true })
    },
  })

  const error = loginMutation.error
  const fieldErrors = isApiError(error) ? error.fieldErrors : {}
  const usernameError = fieldErrors['username']?.[0]
  const passwordError = fieldErrors['password']?.[0]
  const generalError = error
    ? (isApiError(error) && error.status === 400 && (usernameError ?? passwordError)
        ? null
        : error.message)
    : null

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (loginMutation.isPending) return
    loginMutation.mutate({ username: username.trim(), password })
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-console-bg px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <span className="inline-flex size-11 items-center justify-center rounded-md bg-brand text-lg font-bold text-white">
            G
          </span>
          <h1 className="text-2xl font-semibold tracking-tight text-console-text">GTA CRM</h1>
          <p className="text-sm text-console-muted">西藏旅行社 · 销售与订单管理控制台</p>
        </div>

        <div className="console-card px-6 py-6 shadow-sm">
          <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
            {generalError && (
              <Alert variant="destructive">
                <AlertDescription>{generalError}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="username">用户名</Label>
              <Input
                id="username"
                name="username"
                autoComplete="username"
                autoFocus
                required
                value={username}
                aria-invalid={usernameError ? true : undefined}
                onChange={(event) => {
                  setUsername(event.target.value)
                }}
              />
              {usernameError && <p className="text-xs text-status-danger">{usernameError}</p>}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">密码</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                aria-invalid={passwordError ? true : undefined}
                onChange={(event) => {
                  setPassword(event.target.value)
                }}
              />
              {passwordError && <p className="text-xs text-status-danger">{passwordError}</p>}
            </div>

            <Button type="submit" size="lg" disabled={loginMutation.isPending} className="mt-1 w-full">
              {loginMutation.isPending ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : (
                <LockKeyhole className="size-4" />
              )}
              {loginMutation.isPending ? '登录中…' : '登录'}
            </Button>
          </form>

          <div className="mt-5 rounded-md bg-console-hover px-3 py-2.5 text-xs text-console-muted">
            <p>
              演示账号:
              <code className="mx-1 font-mono text-console-text">{DEMO_CREDENTIALS.username}</code>/
              <code className="mx-1 font-mono text-console-text">{DEMO_CREDENTIALS.password}</code>
            </p>
            <button
              type="button"
              className="console-link-action mt-1"
              onClick={() => {
                setUsername(DEMO_CREDENTIALS.username)
                setPassword(DEMO_CREDENTIALS.password)
              }}
            >
              一键填入演示账号
            </button>
          </div>
        </div>

        <p className="mt-4 text-center text-xs text-console-subtle">
          登录走 Django session + CSRF,凭据只在同源代理内流转。
        </p>
      </div>
    </div>
  )
}
